import type { Wall } from "./types";

export interface DetectedSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface DetectOptions {
  threshold?: number; // 0-255, pixels darker than this count as "ink"
  minLengthRatio?: number; // min run length as fraction of image dimension
  mergeGap?: number; // px gap allowed to merge collinear runs
  collinearTolerance?: number; // px perpendicular distance to merge parallel lines
}

/**
 * Detect orthogonal wall-like line segments from a blueprint image by scanning
 * rows and columns for long runs of dark pixels.
 */
export async function detectWallsFromImage(
  src: string,
  bp: { x: number; y: number; w: number; h: number },
  opts: DetectOptions = {},
): Promise<Omit<Wall, "id">[]> {
  const threshold = opts.threshold ?? 110;
  const minLengthRatio = opts.minLengthRatio ?? 0.08;
  const mergeGap = opts.mergeGap ?? 4;
  const collinearTol = opts.collinearTolerance ?? 4;

  const img = await loadImage(src);
  const maxDim = 700;
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const W = Math.max(1, Math.round(img.width * scale));
  const H = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return [];
  ctx.drawImage(img, 0, 0, W, H);
  const data = ctx.getImageData(0, 0, W, H).data;

  // Binary mask of dark pixels
  const dark = new Uint8Array(W * H);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const v = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
    dark[p] = v < threshold ? 1 : 0;
  }

  const minLenPx = Math.max(20, Math.round(Math.min(W, H) * minLengthRatio));

  type Run = { axis: "h" | "v"; fixed: number; a: number; b: number };
  const runs: Run[] = [];

  // Horizontal scan
  for (let y = 0; y < H; y++) {
    let start = -1;
    let gap = 0;
    for (let x = 0; x <= W; x++) {
      const isDark = x < W && dark[y * W + x] === 1;
      if (isDark) {
        if (start === -1) start = x;
        gap = 0;
      } else if (start !== -1) {
        gap++;
        if (gap > mergeGap || x === W) {
          const end = x - gap;
          if (end - start >= minLenPx) runs.push({ axis: "h", fixed: y, a: start, b: end });
          start = -1;
          gap = 0;
        }
      }
    }
  }
  // Vertical scan
  for (let x = 0; x < W; x++) {
    let start = -1;
    let gap = 0;
    for (let y = 0; y <= H; y++) {
      const isDark = y < H && dark[y * W + x] === 1;
      if (isDark) {
        if (start === -1) start = y;
        gap = 0;
      } else if (start !== -1) {
        gap++;
        if (gap > mergeGap || y === H) {
          const end = y - gap;
          if (end - start >= minLenPx) runs.push({ axis: "v", fixed: x, a: start, b: end });
          start = -1;
          gap = 0;
        }
      }
    }
  }

  // Merge near-parallel collinear runs
  const merged: Run[] = [];
  const horiz = runs.filter((r) => r.axis === "h").sort((a, b) => a.fixed - b.fixed || a.a - b.a);
  const vert = runs.filter((r) => r.axis === "v").sort((a, b) => a.fixed - b.fixed || a.a - b.a);

  function mergeAxis(list: Run[]) {
    const out: Run[] = [];
    for (const r of list) {
      const found = out.find(
        (o) => Math.abs(o.fixed - r.fixed) <= collinearTol && !(r.b < o.a - mergeGap || r.a > o.b + mergeGap),
      );
      if (found) {
        found.a = Math.min(found.a, r.a);
        found.b = Math.max(found.b, r.b);
        found.fixed = Math.round((found.fixed + r.fixed) / 2);
      } else {
        out.push({ ...r });
      }
    }
    return out;
  }
  merged.push(...mergeAxis(horiz), ...mergeAxis(vert));

  // Map from image px -> world cm (blueprint coords)
  const sx = bp.w / W;
  const sy = bp.h / H;

  return merged.map((r) => {
    if (r.axis === "h") {
      return {
        x1: bp.x + r.a * sx,
        y1: bp.y + r.fixed * sy,
        x2: bp.x + r.b * sx,
        y2: bp.y + r.fixed * sy,
        thickness: 8,
      };
    }
    return {
      x1: bp.x + r.fixed * sx,
      y1: bp.y + r.a * sy,
      x2: bp.x + r.fixed * sx,
      y2: bp.y + r.b * sy,
      thickness: 8,
    };
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function polygonArea(points: { x: number; y: number }[]): number {
  let a = 0;
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    a += p1.x * p2.y - p2.x * p1.y;
  }
  return Math.abs(a) / 2;
}

export function polygonPerimeter(points: { x: number; y: number }[]): number {
  let p = 0;
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    p += Math.hypot(p2.x - p1.x, p2.y - p1.y);
  }
  return p;
}

export function polygonCentroid(points: { x: number; y: number }[]): { x: number; y: number } {
  let x = 0, y = 0;
  for (const p of points) { x += p.x; y += p.y; }
  return { x: x / points.length, y: y / points.length };
}